// Refunds, and the one decision an owner must make here: a refund the
// system raised for a duplicate or late payment sends nothing until a person
// approves it (ADR 0045). The buttons exist only for someone who may
// `integration:manage`; the server enforces it regardless.

import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import type { PosRefund, RefundIntentState } from '../../api/pos-types.js';
import { Chip, QueryState, type TabProps } from './common.js';
import { formatDateTime, formatRupees, humanize } from './format.js';
import { REFUND_REASON_TEXT, textOr } from './labels.js';

const STATES: RefundIntentState[] = [
  'pending_approval',
  'pending',
  'in_progress',
  'refunded',
  'partially_refunded',
  'failed',
  'rejected',
];

const TONE: Partial<Record<RefundIntentState, 'ok' | 'warn' | 'bad'>> = {
  pending_approval: 'warn',
  refunded: 'ok',
  failed: 'bad',
  partially_refunded: 'warn',
};

export interface RefundsTabProps extends TabProps {
  readonly canDecide: boolean;
}

function decisionError(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 403) return 'You do not have permission to decide refunds.';
    if (err.status === 409)
      return 'Someone already decided this refund. The list has been refreshed.';
    if (err.status === 0) return 'Network error — check your connection.';
  }
  return 'Could not record that decision. Try again.';
}

export function RefundsTab({ orgId, outletId, canDecide }: RefundsTabProps): JSX.Element {
  const qc = useQueryClient();
  const [state, setState] = useState<string>('');
  const [note, setNote] = useState('');
  const key = ['pos-refunds', orgId, outletId] as const;

  const refunds = useQuery({
    queryKey: [...key, state],
    queryFn: () =>
      apiFetch<{ refunds: PosRefund[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/refunds${state ? `?state=${state}` : ''}`,
      ),
  });

  const decide = useMutation({
    mutationFn: (args: { id: string; decision: 'approve' | 'reject' }) =>
      apiFetch<PosRefund>(
        `/organizations/${orgId}/outlets/${outletId}/pos/refunds/${args.id}/decision`,
        {
          method: 'POST',
          body: { decision: args.decision, ...(note.trim() ? { note: note.trim() } : {}) },
        },
      ),
    onSettled: () => {
      setNote('');
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['pos-day', orgId, outletId] });
    },
  });
  const error = decisionError(decide.error);

  return (
    <section aria-labelledby="refunds-heading">
      <h2 id="refunds-heading">Refunds</h2>
      <label className="inline-field">
        State{' '}
        <select value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">All</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {humanize(s)}
            </option>
          ))}
        </select>
      </label>
      <QueryState loading={refunds.isLoading} error={refunds.error} />
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      {refunds.data && refunds.data.refunds.length === 0 ? (
        <p className="muted">No refunds for this selection.</p>
      ) : null}
      <ul className="refund-list">
        {(refunds.data?.refunds ?? []).map((r) => (
          <li key={r.id} className="card refund-row">
            <div className="refund-head">
              <strong>{formatRupees(r.amountPaise)}</strong>
              <Chip tone={TONE[r.state]}>{humanize(r.state)}</Chip>
              <span className="muted">
                {textOr(REFUND_REASON_TEXT, r.reason)} ·{' '}
                {r.origin === 'system' ? 'raised by the system' : 'asked by the till'} ·{' '}
                {formatDateTime(r.createdAt)}
              </span>
            </div>
            {r.allocations.length > 0 ? (
              <ul className="muted">
                {r.allocations.map((a) => (
                  <li key={a.id}>
                    {formatRupees(a.amountPaise)} back to one payment · {a.status}
                    {a.failureCode ? ` (${humanize(a.failureCode)})` : ''}
                  </li>
                ))}
              </ul>
            ) : null}
            {r.state === 'pending_approval' ? (
              <div className="refund-decision">
                <p>
                  Nothing is sent until you approve. Rejecting leaves the money with the customer’s
                  original payment, unrefunded.
                </p>
                {canDecide ? (
                  <>
                    <label className="inline-field">
                      Note (optional){' '}
                      <input
                        type="text"
                        maxLength={200}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </label>
                    <div className="row-actions">
                      <button
                        type="button"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, decision: 'approve' })}
                      >
                        Approve refund
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: r.id, decision: 'reject' })}
                      >
                        Reject
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="muted">Only an owner can decide this.</p>
                )}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
